import ReportSections from './../constants/ReportSections';
import Logger from './../../../logger';
import TeamRolePerspectives from '../constants/TeamRolePerspectives';
import PersonaFitRoles from './../components/PersonaFitRozet/constants/PersonaFitRoles';
import ProductTypes from './../../Report/constants/ProductTypes';

/**
 * ReportHelper
 */

const ReportHelper = {
    getParticipant(participantData) {
        return {
            firstName: participantData.firstName,
            infix: participantData.infix,
            lastName: participantData.lastName,
            language: participantData.language,
            educationLevel: participantData.educationLevel,
            appointmentDate: participantData.appointmentDate,
            id: participantData.id,
            phase: participantData.phase
        };
    },

    getProduct(productData) {
        return {
            productName: productData.productName,
            productHandle: productData.productHandle,
            translationKey: productData.translationKey,
            isOnlineProduct: productData.isOnlineProduct
        };
    },

    getProject(projectData) {
        return {
            projectName: projectData.projectName,
            projectDisplayName: projectData.projectDisplayName,
            projectType: projectData.projectType
        };
    },

    getOrganisation(organisationData) {
        return {
            organisationName: organisationData.organisationName,
            logo: organisationData.logo
        };
    },

    getConsultant(consultantData) {
        return {
            consultantName: consultantData.consultantName
        };
    },

    /**
     * returns provided score type extracted from scores with given decimals
     * @param {array} scores: scores
     * @param {string} type: type of score
     * @param {number} toFixed: determines how to round the score
     * @returns {number|null} score
     */
    getScoreType(scores, type, toFixed) {
        let scoreType = null;

        if (scores && scores[type]) {
            try {
                scoreType = scores[type].toFixed(toFixed);
            } catch (e) {
                Logger.instance.error({
                    component: 'report',
                    message: 'could not parse score type'
                });
            }
        }

        return scoreType;
    },

    /**
     * parse report section
     * @param {Object|array} reportData reportData
     * @param {Object|array} newState newState
     * @returns {Object|array} report section
     */
    getUIConfig(reportData, newState) {
        const UIConfig = reportData.config;
        const sections = reportData.sections;
        const parsedUIConfigs = [];

        if (UIConfig) {
            UIConfig.forEach(sectionConfig => {

                // parse section
                if (sections[sectionConfig.handle]) {
                    const section = sections[sectionConfig.handle];

                    switch (sectionConfig.handle) {
                        case ReportSections.DECISION_MODULE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle || '',
                                title: section.name || '',
                                basedOn: section.basedOn || '',
                                outcome: section.outcome || '',
                                type: section.type || '',
                                translatedOutcome: section.translatedOutcome || '',
                                outcomeHeader: section.outcomeHeader || '',
                                outcomeText: section.outcomeText || '',
                                intellectualCapability: section.intellectualCapability || null,
                                competencies: section.competencies || [],
                                completed: section.completed || false,
                                selfForwardingToNextPhase: section.selfForwardingToNextPhase || false,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;
                        case ReportSections.ABILITY_TO_CHANGE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                evaluation: section.evaluation || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                summaryName: section.summaryName || section.name || ''
                            };
                            break;

                        case ReportSections.HEXIT:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                scores: section.scores || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.INTELLECTUAL_CAPABILITY:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                scoreByConsultant: section.scoreByConsultant || '',
                                evaluationByConsultant: section.evaluationByConsultant || '',
                                participantRequestedLevel: section.participantRequestedLevel || '',
                                cutOff: section.cutOff || '',
                                definition: section.definition || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                summaryName: section.summaryName || section.name || '',
                                outcome: section.outcome || null
                            };
                            break;

                        case ReportSections.INTELLECTUAL_CAPABILITY_EXTENDED:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                cutOff: section.cutOff || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                definition: section.definition || '',
                                scoreByConsultant: section.scoreByConsultant || '',
                                evaluationByConsultant: section.evaluationByConsultant || '',
                                participantRequestedLevel: section.participantRequestedLevel || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.COLLABORATION:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                definition: section.definition || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.WORK_APPROACH:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                definition: section.definition || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.FLEXIBILITY_TO_CHANGE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                definition: section.definition || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.INTELLECTUAL_CAPABILITY_MBO:
                            newState.reportData.sections[sectionConfig.handle] = {
                                title: section.name || '',
                                introduction: section.introduction || '',
                                handle: section.handle,
                                basedOn: section.basedOn || '',
                                score: section.score || '',
                                scoreTypes: section.scoreTypes || {},
                                scores: section.scores || '',
                                evaluation: section.evaluation || '',
                                definition: section.definition || '',
                                mboLevel: section.mboLevel || '',
                                cutOff: section.cutOff || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.STRONG_POINTS:
                        case ReportSections.POINTS_OF_ATTENTION:
                        case ReportSections.DEVELOPMENT_ADVICE:
                        case ReportSections.RESEARCH_QUESTION:

                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name || '',
                                introduction: section.introduction || '',
                                text: section.text || '',
                                autoCalculatedText: section.autoCalculatedText || '',
                                basedOn: section.basedOn || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                summaryName: section.summaryName || section.name || ''
                            };
                            break;

                        case ReportSections.PERSONALITY:
                        case ReportSections.INFLUENCING_STYLES:
                        case ReportSections.MOTIVES:
                        case ReportSections.WPQ:
                        case ReportSections.WORKING_STYLES:
                        case ReportSections.PERSONALITY_BUILDING_DOCKS:
                        case ReportSections.WORK_ENVIRONMENT:
                        case ReportSections.ABILITY_TO_CHANGE_CALCULATED:

                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                scores: section.scores,
                                basedOn: section.basedOn || '',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                summaryName: section.summaryName || section.name || '',
                                outcome: section.outcome || null
                            };
                            break;

                        case ReportSections.RISK_INDICATION:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                scores: section.scores,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                basedOn: section.basedOn || ''
                            };
                            break;

                        case ReportSections.SELECTION_ADVICE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                scores: section.scores,
                                text: section.text,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                basedOn: section.basedOn || '',
                                summaryName: section.summaryName || section.name || ''
                            };
                            break;

                        case ReportSections.TEAM_ROLES_DEVELOPMENT:
                        case ReportSections.TEAM_ROLES:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                basedOn: section.basedOn || '',
                                scores: section.scores,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.COMPETENCY_PROFILE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                competencies: section.competencies,
                                basedOn: section.basedOn || '',
                                introduction: section.introduction || '',
                                title: section.name || '',
                                label: section.label || '',
                                translationKeyName: section.translationKeyName || '',
                                extended: sectionConfig.sectionVariant && sectionConfig.sectionVariant === 'extended',
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                comment: section.comment || '',
                                summaryName: section.summaryName || section.name || ''
                            };

                            break;

                        case ReportSections.REFLECTION:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                text: section.text,
                                forwardCoaching: section.forwardCoaching ? section.forwardCoaching : false,
                                isMandatory: section.isMandatory || false,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                basedOn: section.basedOn || '',
                                summaryName: section.summaryName || section.name || ''
                            };
                            break;

                        case ReportSections.REFLECTION_WITH_EXERCISES:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                text: section.text,
                                forwardCoaching: section.forwardCoaching ? section.forwardCoaching : false,
                                exercises: section.exercises,
                                isMandatory: section.isMandatory || false,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false,
                                basedOn: section.basedOn || ''
                            };
                            break;

                        case ReportSections.DEALING_WITH_RESISTANCE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                scores: section.scores,
                                basedOn: section.basedOn || '',
                                footerTitle: section.footerTitle,
                                score: section.finalScore,
                                evaluation: section.evaluation,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.WORK_SAFELY:
                            newState.reportData.sections[sectionConfig.handle] = {
                                handle: section.handle,
                                title: section.name,
                                introduction: section.introduction || '',
                                scores: section.scores,
                                basedOn: section.basedOn || '',
                                footerTitle: section.footerTitle,
                                score: section.finalScore,
                                evaluation: section.evaluation,
                                placeholder: section.placeholder || false,
                                editable: section.editable || false
                            };
                            break;

                        case ReportSections.CRITICAL_THINKING:
                            newState.reportData.sections[sectionConfig.handle] = {
                                ...section
                            };
                            break;

                        case ReportSections.LSR_CONCLUSION:
                            newState.reportData.sections[sectionConfig.handle] = {
                                ...section
                            };
                            break;

                        case ReportSections.SELECTION_DEVELOPMENT_ADVICE:
                            newState.reportData.sections[sectionConfig.handle] = {
                                ...section,
                                title: section.name,
                                summaryName: section.summaryName || section.name || ''
                            };
                            break;

                        default:
                            break;
                    }

                    // only parse ui config for a section if the section itself is returned from the api
                    if (newState.reportData.sections[sectionConfig.handle]) {

                        // parse ui config, this is necessary for the grid, which is compatible with react-grid-layout
                        // i: index/identifier (handle), used as keys for child elements in the grid.
                        // Could be an index (number), but for us the section handle is more descriptive
                        // w: width
                        // h: height
                        // x: x position of grid
                        // y: y position of grid
                        // isResizable: can be resized
                        parsedUIConfigs.push({
                            i: sectionConfig.handle,
                            w: sectionConfig.width,
                            h: sectionConfig.height,
                            x: sectionConfig.x,
                            y: sectionConfig.y,
                            isResizable: false,
                            order: sectionConfig.order || null,
                            visibility: sectionConfig.visibility
                        });
                    }


                }
            });
        }

        return parsedUIConfigs;
    },

    getCompetencyName(competency, languageId, i18nParticipation = null) {
        if (!competency) {
            return null;
        }

        // return alias
        if (languageId &&
            competency.competencyAliases &&
            competency.competencyAliases[languageId] &&
            competency.competencyAliases[languageId].name
        ) {
            return competency.competencyAliases[languageId].name;
        }

        // return API v2 alias
        if (languageId &&
            competency.aliases &&
            competency.aliases[languageId] &&
            competency.aliases[languageId].competencyAliasName
        ) {
            return competency.aliases[languageId].competencyAliasName;
        }

        // return translated label by translationKey
        if (i18nParticipation &&
            competency.translationKey &&
            i18nParticipation[`competencies_${competency.translationKey}`]
        ) {
            return i18nParticipation[`competencies_${competency.translationKey}`];
        }

        // return translated label by translationKeyName
        if (i18nParticipation &&
            competency.translationKeyName &&
            i18nParticipation[`competencies_${competency.translationKeyName}`]
        ) {
            return i18nParticipation[`competencies_${competency.translationKeyName}`];
        }

        // return untranslated name or label
        return competency.name || competency.label;
    },

    getCompetencyDefinition(competency, languageId, i18nParticipation = null) {
        if (!competency && (!competency.definition || !competency.competencyDefinition)) {
            return null;
        }

        // return alias definition
        if (languageId &&
            competency.competencyAliases &&
            competency.competencyAliases[languageId] &&
            competency.competencyAliases[languageId].definition
        ) {
            return competency.competencyAliases[languageId].definition;
        }

        // return API v2 alias definition
        if (languageId &&
            competency.aliases &&
            competency.aliases[languageId] &&
            competency.aliases[languageId].competencyAliasDefinition
        ) {
            return competency.aliases[languageId].competencyAliasDefinition;
        }

        // return translated label by translationKey
        if (i18nParticipation &&
            competency.translationKey &&
            i18nParticipation[`competencies_${competency.translationKey}_definition`]
        ) {
            return i18nParticipation[`competencies_${competency.translationKey}_definition`];
        }

        // return translated label by translationKeyDefinition
        if (i18nParticipation &&
            competency.translationKeyDefinition &&
            i18nParticipation[`competencies_${competency.translationKeyDefinition}`]
        ) {
            return i18nParticipation[`competencies_${competency.translationKeyDefinition}`];
        }

        // return untranslated definition
        return competency.definition || competency.competencyDefinition;
    },

    /**
     * Returns the constant identifier for the current score key from roles
     * @param {string} key - key to search for
     * @param {Object} roles - collection of roles
     * @returns {string} string of relevant identifier
     */
    getIdentifierForScoreKey(key, roles) {
        let identifierForKey = null;

        Object.keys(roles).forEach(role => {
            Object.keys(roles[role]).forEach(identifier => {
                if (roles[role][identifier] === key) {
                    identifierForKey = identifier;
                }
            });
        });

        return identifierForKey;
    },

    /**
     * Returns score object with role identifier for given key in given array of scores
     * @param {string} key - key to search for
     * @param {array} array - collection of scores
     * @param {Object} roles - collection of roles
     * @returns {Object} score object
     */
    findScoreObjectInArrayByName(key, array, roles) {
        let scoreReturnedObj = {};

        array.forEach(scoreObject => {
            if (scoreObject.name === key) {
                scoreReturnedObj = {
                    key: scoreObject.name,
                    value: scoreObject.score,
                    label: this.getIdentifierForScoreKey(scoreObject.name, roles),
                    definition: scoreObject.definition || ''
                };
            }
        });

        return scoreReturnedObj;
    },

    /**
     * Returns score objects with score labels from the given scores collection that match the given perspective
     * @param {Array} scores - array of score objects
     * @param {string} perspective - infix to search for
     * @param {Object} roles - object with roles
     * @returns {array} array with relevant scores
     */
    extractScoresForTeamRolePerspective(scores, perspective, roles) {
        const tempArray = [];
        let rolesForPerspective = null; // determine which role perspective (toBe/toWant) will be used to extract the score identifiers
        let translatedInfix = null;

        if (perspective === 'zij') {
            translatedInfix = 'TO_BE';
            rolesForPerspective = PersonaFitRoles.ToBe;
        } else if (perspective === 'wil') {
            translatedInfix = 'TO_WANT';
            rolesForPerspective = PersonaFitRoles.ToWant;
        }

        const scoreNames = [
            rolesForPerspective[`${translatedInfix}_GO_GETTER`],
            rolesForPerspective[`${translatedInfix}_EXPERT`],
            rolesForPerspective[`${translatedInfix}_CONTROLLER`],
            rolesForPerspective[`${translatedInfix}_COORDINATOR`],
            rolesForPerspective[`${translatedInfix}_CONNECTOR`],
            rolesForPerspective[`${translatedInfix}_INSPIRATOR`],
            rolesForPerspective[`${translatedInfix}_INNOVATOR`],
            rolesForPerspective[`${translatedInfix}_ENTREPRENEUR`]
        ];

        scoreNames.forEach(scoreName => {
            tempArray.push(this.findScoreObjectInArrayByName(scoreName, scores, roles));
        });

        // to ensure the scores are always parsed in the same order, first sort them alphabetically
        // tempArray = (Utils.alphabeticallySortFieldInArray(tempArray, 'key'));
        return tempArray;
    },

    /**
     * Returns a sorted array of score items in desc order
     * @param {Array} scores - array of score objects
     * @param {string} perspective - infix to search for
     * @param {Object} roles - object with roles
     * @param {number} total - number of itmes desired to be returned
     * @returns {Array} of hightes scores
     */
    getHighestScores(scores, perspective, roles, total = 1) {
        const tempArray = [];
        let sortedItems = [];
        let rolesForPerspective = null; // determine which role perspective (toBe/toWant) will be used to extract the score identifiers
        let translatedInfix = null;

        if (perspective === 'zij') {
            translatedInfix = 'TO_BE';
            rolesForPerspective = PersonaFitRoles.ToBe;
        } else if (perspective === 'wil') {
            translatedInfix = 'TO_WANT';
            rolesForPerspective = PersonaFitRoles.ToWant;
        }

        const scoreNames = [
            rolesForPerspective[`${translatedInfix}_GO_GETTER`],
            rolesForPerspective[`${translatedInfix}_EXPERT`],
            rolesForPerspective[`${translatedInfix}_CONTROLLER`],
            rolesForPerspective[`${translatedInfix}_COORDINATOR`],
            rolesForPerspective[`${translatedInfix}_CONNECTOR`],
            rolesForPerspective[`${translatedInfix}_INSPIRATOR`],
            rolesForPerspective[`${translatedInfix}_INNOVATOR`],
            rolesForPerspective[`${translatedInfix}_ENTREPRENEUR`]
        ];

        scoreNames.forEach(scoreName => {
            tempArray.push(this.findScoreObjectInArrayByName(scoreName, scores, roles));
        });

        sortedItems = tempArray
            .sort((a, b) => a.value - b.value)
            .reverse()
            .slice(0, total);

        return sortedItems;
    },

    /**
     * Returns a translated string with desired nr of top scores keywords
     * eg: '<b>entrepreneur ,</b> <b>specialist  </b>and <b>connector  </b>'
     * @param {string} perspective - infix to search for
     * @param {Array} sectionScores - array of score objects
     * @param {Object} i18nParticipation - translations object
     * @returns {string|null} - footer string of most highest scores labels
     */
    getTeamRolesDevelopmentFooterString(perspective, sectionScores, i18nParticipation = null) {
        let scores = [];
        let footerString = '';

        if (!perspective || !sectionScores) {
            return null;
        }

        if (perspective === TeamRolePerspectives.PERSONALITY) {
            scores = ReportHelper.getHighestScores(sectionScores, TeamRolePerspectives.PERSONALITY, PersonaFitRoles, 3);
        } else if (perspective === TeamRolePerspectives.AMBITION) {
            scores = ReportHelper.getHighestScores(sectionScores, TeamRolePerspectives.AMBITION, PersonaFitRoles, 3);
        }

        // add comma after each keyword except for the second last one because is before 'and' keyword
        // if last item in array add 'and' keyword
        scores.forEach((item, idx) => {
            const lastItem = idx === scores.length - 1;
            const secondLastItem = idx === scores.length - 2;
            const divider = lastItem || secondLastItem ? ' ' : ',';
            const key = `report_rozet_${item.label.toLowerCase()}`;

            if (lastItem) {
                footerString += `${i18nParticipation.report_rozet_footer_part_b}`;
            }

            footerString += `&nbsp;<b>${i18nParticipation[key].toLowerCase()} ${divider}</b>`;
        });

        return footerString;
    },

    // todo: tech debt: this helper and the logic that uses it can be removed once 'star ranking' is one
    //  of the scorebar types available to choose from in appendixscorebarsection. it would be even better
    //  if backend could return which scorebar type to use across products and report types.
    shouldUseStarRanking(productKey) {
        switch (productKey) {
            case ProductTypes.TALENT_FIT:
            case ProductTypes.TALENT_SCAN:
            case ProductTypes.TALENT_NAVIGATOR:
            case ProductTypes.TEAM_AND_ORGANISATION:
                return false;

            default:
                return true;
        }
    }
};

export default ReportHelper;
